/**
 * Correlator definitions and helpers
 * @see Correlator
 * @see AnnotatedAbstract
 */
import * as _ from 'lodash'
import Annotations, { Annotation, ENTITY_PRECEDENCE } from './Annotations'
import AnnotatedAbstract from './AnnotatedAbstract'
import { FOLLOW_UP_SENSES, GROUP_SENSES } from './DocSearch'

const distance = (a, b) =>
	a.i < b.i ? b.start - a.end : a.start - b.end // can be negative if the quantifier is *within* the intervention annotation

const distanceThreshold = n =>
	(a, b, cur) =>
		Math.abs(distance(a, b)) > n ? null : cur

const precedesMult = n =>
	(a, b, cur) => {
		return a.i < b.i ? (cur * n) : cur
	}

const layerWeight = (a, b, cur) => cur * (1 / (a.layer + 1))
const inverseDist = (a, b, cur) => cur * (1 / Math.abs(distance(a,b)))
const typeMult = (types, mult) => (a, b, cur) => types.includes(a.type) ? (cur * mult) : cur

const negativeDistanceMult = n =>
	(a, b, cur) => {
		const dist = distance(a, b)
		return dist < 0 ? (cur * n) : cur
	}

const weight = (...fns) =>
	(a, b) => {
		let weight = 1
		for (const fn of fns) {
			weight = fn(a, b, weight)
			if (weight == undefined) return
		}
		return { a, b, weight }
	}

const quantifierWeight = weight(distanceThreshold(50), layerWeight, inverseDist, precedesMult(0.5), negativeDistanceMult(4))
// weight both measures and group-ns, but strongly prefer explicit group-ns over arbitrary measures
const groupNWeight = weight(distanceThreshold(50), layerWeight, inverseDist, precedesMult(0.5), negativeDistanceMult(4), typeMult(['group-n'], 4))

/**
 * Correlators try to infer relationshiops and correlate various annotations with each other, e.g. numeric quantities with
 * interventions, outcomes, timepoints, etc.
 */
export interface Correlator {
	correlate(annotated: AnnotatedAbstract, annotations: Annotation[]): any[]
}

function findBestRelations(annotations, sources, weightFn) {
	let weights = []
	for (const a of sources) {
		const [preceding, following] = _.partition(annotations, r => r.i < a.i)
		for (const r of preceding) {
			const w = weightFn(r, a)
			if (w) weights.push(w)
		}
		for (const r of following) {
			const w = weightFn(r, a)
			if (w) weights.push(w)
		}
	}
	weights = _.sortBy(weights, w => -w.weight)
	const best = []
	const seen = new Set()
	for (const weight of weights) {
		if (seen.has(weight.a)) continue
		best.push(weight)
		seen.add(weight.a)
	}
	return best
}

const unassigned = (assignField) => a => (!a[assignField] || (Array.isArray(a[assignField] && !a[assignField].length)))
const and = (f, g) => (...args) => f(...args) && g(...args)
const or = (f, g) => (...args) => f(...args) || g(...args)
const typeFilter = (sourceTypes) => a => _.some(a.types, t => sourceTypes.includes(t))

const keepFirstMeasure = annot => {
	for (const other of annot.measures.slice(1)) {
		other.parent = undefined
	}
	annot.measures = [annot.measures[0]]
}

class RelateAnnotations {
	correlate(annotated: AnnotatedAbstract, annotations, sourceFilter, relatedFilter, weightFn, assignField, inversField = 'parent', multiple = true, overwrite = false): any[] {
		if (!overwrite) {
			sourceFilter = and(sourceFilter, unassigned(assignField))
		}
		const sources = annotations.filter(a => sourceFilter(a))
		const sentences = _.uniq(sources.map(i => i.sentence))
		const annots = new Annotations(annotations)
		annots.sort()
		// all target annotations that are not themselves already assigned to a parent or conflict with another annotation
		// assigned to a parent
		const filtered = annotations.filter(a => {
			if (a.parent || !relatedFilter(a) || !sentences.includes(a.sentence)) return false
			const overlapping = annots.findOverlaps(a, true).filter(an => !['section', 'sentence'].includes(an.kind))
			const curPrecedence = Math.max(...a.types.map(t => (ENTITY_PRECEDENCE[t] ?? -Infinity)))
			return !_.some(overlapping, existing =>
				existing.parent || _.some(existing.types, t => (ENTITY_PRECEDENCE[t] ?? -Infinity) > curPrecedence)
			)
		})
		const rels = findBestRelations(filtered, sources, weightFn)
		const changed = new Set()
		for (const rel of rels) {
			rel.a[inversField] = rel.b
			if (multiple) {
				if (!changed.has(rel.b) && overwrite) {
					const others = rel.b[assignField]
					if (others) {
						for (const other of rel.b[assignField]) {
							other[inversField] = undefined
						}
						rel.b[assignField] = []
					}
				}
				(rel.b[assignField] || (rel.b[assignField] = [])).push(rel.a)
			} else {
				if (rel.b[assignField] && overwrite) {
					rel.b[assignField][inversField] = undefined
				}
				if (!rel.b[assignField]) {
					rel.b[assignField] = rel.a
				}
			}
			changed.add(rel.a)
			changed.add(rel.b)
		}
		return [..._.partition(sources, s => changed.has(s)), ..._.partition(filtered, s => changed.has(s))]
	}
}

const senseFilter = (senses) => a => a.senses && a.senses.some(s => senses.includes(s))

export class RelateInterventionQuantifiers extends RelateAnnotations implements Correlator {
	correlate(annotated: AnnotatedAbstract, annotations: Annotation[]) {
		return super.correlate(annotated, annotations, typeFilter(['intervention']), typeFilter(['dosage']), quantifierWeight, 'measures')
	}
}
export class RelateInterventionGroupN extends RelateAnnotations implements Correlator {
	correlate(annotated: AnnotatedAbstract, annotations: Annotation[]) {
		return super.correlate(annotated, annotations, or(typeFilter(['intervention']), senseFilter([GROUP_SENSES])), typeFilter(['group-n']), groupNWeight, 'groupn', 'parent', false)
	}
}
export class RelateInterventionGroupNPosNumbers extends RelateAnnotations implements Correlator {
	correlate(annotated: AnnotatedAbstract, annotations: Annotation[]) {
		const result = super.correlate(annotated, annotations, or(typeFilter(['intervention']), senseFilter([GROUP_SENSES])), typeFilter(['pos-int']), groupNWeight, 'groupn', 'parent', false)
		// const [c,u,changedRelated] = result
		// for (const r of changedRelated) {
		//   // update the related annotation type to the more-specific 'group-n'
		//   if (r.type != 'group-n') r.type = 'group-n'
		// }
		return result
	}
}
export class RelateOutcomeQuantities extends RelateAnnotations implements Correlator {
	correlate(annotated: AnnotatedAbstract, annotations: Annotation[]) {
		return super.correlate(annotated, annotations, typeFilter(['outcome']), typeFilter(['binary-value']), quantifierWeight, 'measures')
	}
}
export class RelateTimePointQuantities extends RelateAnnotations implements Correlator {
	correlate(annotated: AnnotatedAbstract, annotations: Annotation[]) {
		const result = super.correlate(annotated, annotations, senseFilter(FOLLOW_UP_SENSES),
			typeFilter(['timepoint']), quantifierWeight, 'measures', 'parent', true, true)
		const [changedSrcs, unchangedSrcs, changedRelated, unchangedRelated] = result
		for (const a of changedSrcs) {
			if (a.type != 'timepoint') {
				keepFirstMeasure(a)
			}
		}
		unchangedRelated.forEach(a => a.visual = false)
		return result
	}
}